import MediaImage, {
  MediaImageData,
} from "@/components/atoms/media/MediaImage";
import SvgInfoIcon from "@/components/svg/InfoIcon";
import { buildImageUrl } from "@/utils/imageSharpUrlBuilder";
import {
  Box,
  chakra,
  Grid,
  IconButton,
  LinkBox,
  LinkOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { PortfolioItemModal } from "../portfolioItemModal";
import DOMPurify from "isomorphic-dompurify";

export interface PortfolioItemData {
  title?: string;
  introduction?: string;
  url: string;
  mediaItem?: MediaImageData;
  type?: string;
  location?: string;
  surface?: string;
  status?: string;
}

export interface PortfolioItemProps {
  item: PortfolioItemData;
}

export default chakra(PortfolioItem);
function PortfolioItem({ item }: PortfolioItemProps) {
  const t = useTranslations();
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <LinkBox mt={2} mb={4}>
        <Box display="flex" justifyContent="space-between">
          <Box>
            <LinkOverlay href={item.url}>
              {item.title && (
                <Text
                  textStyle="label.md"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(item.title, {
                      ALLOWED_TAGS: ["strong"],
                    }),
                  }}
                  sx={{
                    strong: {
                      display: "block",
                      textStyle: "label.lg",
                      fontWeight: "bold",
                    },
                  }}
                />
              )}
            </LinkOverlay>
          </Box>
          <IconButton
            onClick={onOpen}
            aria-label="Open"
            icon={<SvgInfoIcon />}
            bgColor="transparent"
            _hover={{ bgColor: "transparent" }}
            _active={{ bgColor: "transparent" }}
          />
        </Box>
        {item.mediaItem && (
          <MediaImage
            {...item.mediaItem}
            viewportWidthFactor={{ base: 1 }}
            cropSizeAlias={{ base: "square" }}
            hasBorderRadius={true}
            imageUrlBuilder={buildImageUrl}
            minW={{ base: "17.5rem", md: "21.25rem", lg: "25rem" }}
            mt={3}
            aspectRatio="1 / 1"
          />
        )}
        <Grid
          templateColumns="auto 1fr"
          columnGap={6}
          rowGap={3}
          mt={6}
          textStyle="label.sm"
          color="gray.600"
        >
          {item.type && (
            <>
              <Text fontWeight={600}>{t("type")}</Text>
              <Text textAlign="right">{item.type}</Text>
            </>
          )}
          {item.location && (
            <>
              <Text fontWeight={600}>{t("location")}</Text>
              <Text textAlign="right">{item.location}</Text>
            </>
          )}
          {item.surface && (
            <>
              <Text fontWeight={600}>{t("rentableFloorArea")}</Text>
              <Text textAlign="right">{item.surface}</Text>
            </>
          )}
          {item.status && (
            <>
              <Text fontWeight={600}>{t("status")}</Text>
              <Text textAlign="right">{item.status}</Text>
            </>
          )}
        </Grid>
      </LinkBox>

      <PortfolioItemModal item={item} isOpen={isOpen} onClose={onClose} />
    </>
  );
}
